import type {
  FilterSelectionMode,
  ScreenerFilterOption,
} from '@api/api/screener';
import type { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import {
  FilterGroups,
  FilterState,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { createFilterId } from '@terminal/views/screener/use-screener-store/filter-utils';

import { ActiveFilterOptions } from '../filters/Filters';

export interface FilterOption extends ScreenerFilterOption {
  childName?: string;
  parentName?: string;
  icon?: SvgIconNames;
}

interface UpdateFilterOptionProps extends ToggleFilterProps {
  existingFilters: ActiveFilterOptions[];
  defaultChildFilterOptions: FilterOption[];
}

export type FilterUseType = 'multi-use' | 'single-use';

const isMatchingFilter = (
  filter: ActiveFilterOptions,
  {
    filterId,
    selectionMode,
    dynamic,
  }: {
    filterId: string;
    selectionMode?: FilterSelectionMode;
    dynamic?: boolean;
  }
) =>
  filter.id === filterId &&
  (dynamic ||
    (filter.selectionMode ?? 'include') === (selectionMode ?? 'include'));

const getSelectionModeProps = (selectionMode?: FilterSelectionMode) =>
  selectionMode ? { selectionMode } : {};

//selected option should always be at 0 index, children are at 1+ index
export const updateFilterOptions = ({
  multiselect,
  existingFilters,
  filterKey,
  option,
  filterId = filterKey,
  defaultChildFilterOptions,
  dynamic,
  childrenOptions,
  selectionMode,
}: UpdateFilterOptionProps): ActiveFilterOptions[] => {
  const existingFilter = existingFilters?.find(filter =>
    isMatchingFilter(filter, { filterId, selectionMode, dynamic })
  );

  const updateOptions = (existingOptions: FilterOption[]) => {
    if (multiselect) {
      return [...existingOptions, option];
    }

    if (option.childName) {
      return existingOptions.reduce((res, o) => {
        let newOption = o.childName === option.childName ? option : o;

        //if key is not included in "include" array fallback to default
        if (o.include && !o.include.some(key => res.some(o => o.key === key))) {
          const defaultOption = defaultChildFilterOptions.find(
            option => option.childName === o.childName
          ) as FilterOption;
          newOption = defaultOption;
        }

        res.push(newOption);
        return res;
      }, [] as FilterOption[]);
    }

    const children =
      childrenOptions ?? existingOptions.filter(o => o.childName);
    return [option, ...children];
  };

  if (existingFilter) {
    return existingFilters.reduce((allFilters, currentFilter) => {
      if (
        isMatchingFilter(currentFilter, {
          filterId,
          selectionMode,
          dynamic,
        })
      ) {
        const options = updateOptions(currentFilter.options);
        const nextSelectionMode = selectionMode ?? currentFilter.selectionMode;
        return [
          ...allFilters,
          {
            id: dynamic ? createFilterId(currentFilter.filter) : filterId,
            filter: currentFilter.filter,
            ...getSelectionModeProps(nextSelectionMode),
            options,
          },
        ];
      }

      return [...allFilters, currentFilter];
    }, [] as ActiveFilterOptions[]);
  }

  const initialOptions = option.childName
    ? defaultChildFilterOptions.map(o =>
        o.childName === option.childName ? option : o
      )
    : [option, ...(childrenOptions ?? defaultChildFilterOptions)];
  const newFilterOption: ActiveFilterOptions = {
    id: dynamic ? createFilterId(filterKey) : filterId,
    filter: filterKey,
    ...getSelectionModeProps(selectionMode),
    options: initialOptions,
  };
  return existingFilters
    ? [...existingFilters, newFilterOption]
    : [newFilterOption];
};

export type ToggleFilterProps = {
  multiselect?: boolean;
  filterKey: string;
  /**
   * have to be passed for dynamic filters, i.e theme_exposure/key_exposure
   */
  filterId?: string;
  dynamic?: boolean;
  option: FilterOption;
  /**
   * used in scenario condition builder to set/update option with children
   */
  childrenOptions?: FilterOption[];
  selectionMode?: FilterSelectionMode;
};

export const getDefaultChildFilters = (
  filterKey: string,
  activeFilters?: FilterState['activeFilters']
): FilterOption[] => {
  const filter = activeFilters?.find(f => f.key === filterKey);

  if (filter?.config?.children) {
    return filter.config.children.map(child => {
      return {
        ...child.options.find(o => o.key === child.default),
        childName: child.name,
      } as FilterOption;
    });
  }

  return [];
};

export const addFilterOption = (
  { activeFilterOptions, activeFilters }: FilterState,
  toggleProps: ToggleFilterProps
) => {
  const { filterKey } = toggleProps;

  const defaultChildFilterOptions = getDefaultChildFilters(
    filterKey,
    activeFilters
  );

  const nextActiveFilterOptions = updateFilterOptions({
    existingFilters: activeFilterOptions,
    defaultChildFilterOptions,
    ...toggleProps,
  });

  return nextActiveFilterOptions;
};

export const setFilterOptions = (
  { activeFilterOptions: existingFilters }: FilterState,
  {
    filterKey,
    options,
    filterId = filterKey,
    selectionMode,
  }: {
    filterKey: string;
    options: ScreenerFilterOption[];
    filterId?: string;
    selectionMode?: FilterSelectionMode;
  }
) => {
  const existingFilter = existingFilters?.find(filter =>
    isMatchingFilter(filter, { filterId, selectionMode })
  );

  if (existingFilter) {
    return existingFilters.reduce((allFilters, currentFilter) => {
      if (!isMatchingFilter(currentFilter, { filterId, selectionMode })) {
        return [...allFilters, currentFilter];
      }

      const nextSelectionMode = selectionMode ?? currentFilter.selectionMode;

      return [
        ...allFilters,
        {
          id: filterId,
          filter: currentFilter.filter,
          ...getSelectionModeProps(nextSelectionMode),
          options,
        },
      ];
    }, [] as ActiveFilterOptions[]);
  }

  const newFilterOption: ActiveFilterOptions = {
    id: filterId,
    filter: filterKey,
    ...getSelectionModeProps(selectionMode),
    options,
  };

  return existingFilters
    ? [...existingFilters, newFilterOption]
    : [newFilterOption];
};

export interface SetIncludeExcludeFilterOptionsProps {
  filterKey: string;
  includedOptions: ScreenerFilterOption[];
  excludedOptions: ScreenerFilterOption[];
}

export const setIncludeExcludeFilterOptions = (
  { activeFilterOptions }: FilterState,
  {
    filterKey,
    includedOptions,
    excludedOptions,
  }: SetIncludeExcludeFilterOptionsProps
): ActiveFilterOptions[] => {
  const unrelatedFilterOptions = activeFilterOptions.filter(
    filterOption => filterOption.filter !== filterKey
  );
  const nextFilterOptions: ActiveFilterOptions[] = [
    { selectionMode: 'include' as const, options: includedOptions },
    { selectionMode: 'exclude' as const, options: excludedOptions },
  ].flatMap(({ selectionMode, options }) =>
    options.length
      ? [{ id: filterKey, filter: filterKey, selectionMode, options }]
      : []
  );

  return [...unrelatedFilterOptions, ...nextFilterOptions];
};

export const removeFilterOption = (
  { activeFilterOptions }: FilterState,
  {
    multiselect,
    filterKey,
    option,
    filterId = filterKey,
    selectionMode,
    dynamic,
  }: ToggleFilterProps
) => {
  const handleMulti = (): ActiveFilterOptions[] => {
    return activeFilterOptions.reduce((a, f) => {
      if (isMatchingFilter(f, { filterId, selectionMode, dynamic })) {
        const filteredOptions = f.options.filter(o => o.key !== option.key);
        const isOnlyChildrenOptions = filteredOptions.every(o => o.childName);

        if (filteredOptions.length && !isOnlyChildrenOptions) {
          a.push({ ...f, options: filteredOptions });
        }
      } else {
        a.push(f);
      }

      return a;
    }, [] as ActiveFilterOptions[]);
  };

  const handleSingle = () =>
    activeFilterOptions.filter(
      filter => !isMatchingFilter(filter, { filterId, selectionMode, dynamic })
    );

  return multiselect ? handleMulti() : handleSingle();
};

export const updateGroup = <T extends string>({
  filterGroups,
  group,
  state,
}: {
  filterGroups: FilterGroups<T>;
  group: T;
  state: Partial<FilterState>;
}) => ({
  ...filterGroups,
  [group]: {
    ...filterGroups[group],
    ...state,
  },
});
